import { useState } from "react";
import axios from "axios";
import { GraduationCap, Crown, Coins, Phone, Users, Share2, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  crown: Crown, coins: Coins, phone: Phone, "graduation-cap": GraduationCap,
  users: Users, share: Share2,
};

export default function Students() {
  const [email, setEmail] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [gradYear, setGradYear] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  // Pull referral code from ?ref=
  const refCode = new URLSearchParams(window.location.search).get("ref") || "";

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/students/verify`, {
        email,
        school_name: schoolName || null,
        grad_year: gradYear ? Number(gradYear) : null,
        referred_by: refCode || null,
      });
      setResult(r.data);
      try { localStorage.setItem("newseze_email", email); } catch (e) {}
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't verify. Use a .edu address.");
    } finally { setBusy(false); }
  };

  const perks = [
    { icon: "crown", label: "Newseze Pro free · 365 days", detail: "Ad-free, premium rewards unlocked, unlimited Boost, premium AI briefs" },
    { icon: "coins", label: "+250 welcome credits", detail: "Read 5 stories, earn $0.50. Built-in beer money." },
    { icon: "phone", label: "Newseze Pulse via SMS at 5pm", detail: "3 headlines, between classes, every weekday" },
    { icon: "graduation-cap", label: "Campus Edition Daily Brief", detail: "Tech · markets · sports · entertainment · careers · mental health" },
    { icon: "users", label: "Campus Ambassador program", detail: "Top referrers on each campus win cash + swag every semester" },
    { icon: "share", label: "TikTok / Instagram share cards", detail: "Auto-rendered trust graphic — share to your story in two taps" },
  ];

  return (
    <>
      <Header onOpenBrief={() => {}} />
      <main className="max-w-[1100px] mx-auto px-5 py-10" data-testid="students-page">
        {/* Hero */}
        <section className="rounded-2xl bg-gradient-to-br from-[#0a1f3d] via-[#1e3a8a] to-[#1e40af] text-white p-8 md:p-10 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8 items-center" data-testid="students-hero">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-black text-[var(--gold)] mb-3">
              <GraduationCap size={13} /> Newseze · Campus Edition
            </div>
            <h1 className="text-[36px] md:text-[46px] font-black tracking-[-0.03em] leading-[1.04]">
              Earn rewards as you read.<br />
              <span className="text-[var(--gold)]">Free for college kids.</span>
            </h1>
            <p className="text-[16px] opacity-90 mt-4 leading-snug max-w-[540px]">
              365 days of Newseze Pro, $0. Earn real credits for every story you read.
              SMS Pulse at 5pm. Share-cards built for TikTok. We pay you to actually be informed.
            </p>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1.5 text-[13.5px]">
              {["Ad-free Newseze Pro · 365 days", "+250 welcome credits", "SMS Pulse · 5pm weekdays", "TikTok / Insta share cards", "Cash out at $5 / 5,000 credits", "Campus Ambassador rewards"].map((p) => (
                <li key={p} className="flex items-start gap-2"><span className="text-[var(--gold)]">✓</span> {p}</li>
              ))}
            </ul>
          </div>

          {/* Verify form */}
          <form onSubmit={submit} className="bg-white text-[var(--ink)] rounded-2xl p-5" data-testid="students-form">
            {result ? (
              <div className="text-center" data-testid="students-success">
                <div className="text-[var(--gold)] text-[36px] mb-2">🎓</div>
                <h3 className="text-[20px] font-black mb-1">You're in.</h3>
                <p className="text-[13.5px] text-[var(--muted)]">
                  Newseze Pro unlocked for <b>{Math.round((new Date(result.tier_expires_at) - new Date()) / 86400000)}</b> days.
                  {result.credits_awarded > 0 && <> +{result.credits_awarded} credits in your wallet.</>}
                </p>
                <a href="/" className="btn primary mt-4 w-full justify-center">
                  Open Campus Edition <ArrowRight size={13} />
                </a>
              </div>
            ) : (
              <>
                <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-2">Verify your .edu</div>
                <label className="block mb-2">
                  <span className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">Your .edu email</span>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                         placeholder="you@university.edu" className="w-full mt-1 border border-[var(--line)] rounded-md p-2.5 text-[14px]"
                         data-testid="students-email" />
                </label>
                <div className="grid grid-cols-[1fr_100px] gap-2 mb-3">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">School</span>
                    <input value={schoolName} onChange={(e) => setSchoolName(e.target.value)}
                           placeholder="(optional)" className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]"
                           data-testid="students-school" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">Grad year</span>
                    <input type="number" min="2024" max="2035" value={gradYear} onChange={(e) => setGradYear(e.target.value)}
                           placeholder="2027" className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]"
                           data-testid="students-grad-year" />
                  </label>
                </div>
                {refCode && (
                  <div className="text-[11px] text-emerald-700 font-black mb-2 bg-emerald-50 rounded-md px-2 py-1.5">
                    ✓ Invited by <span className="font-black">{refCode}</span> — they get +1,000 credits when you verify.
                  </div>
                )}
                {err && <div className="text-[12px] text-rose-700 mb-2" data-testid="students-error">{err}</div>}
                <button type="submit" disabled={busy} className="btn gold w-full justify-center" data-testid="students-submit">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Unlock free Pro
                </button>
                <p className="text-[11px] text-[var(--muted)] text-center mt-2 italic">
                  We send a verification ping to your school address. No card. No upsell.
                </p>
              </>
            )}
          </form>
        </section>

        {/* Perks grid */}
        <section className="mt-8" data-testid="students-perks">
          <div className="section-title"><span className="pip" /><h2>What's included</h2></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
            {perks.map((p, i) => {
              const Icon = ICONS[p.icon] || Sparkles;
              return (
                <div key={i} className="rounded-xl border border-[var(--line)] bg-white p-4" data-testid={`students-perk-${i + 1}`}>
                  <Icon size={18} className="text-[var(--blue)] mb-2" />
                  <div className="text-[13px] font-black text-[var(--ink)] leading-snug">{p.label}</div>
                  <p className="text-[12px] text-[var(--muted)] mt-1 leading-snug">{p.detail}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Ambassador section */}
        <section className="mt-8 rounded-2xl border border-[var(--gold)] bg-[#fff7d6] p-6" data-testid="students-ambassador">
          <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[#7a5b00]">Newseze Campus Ambassador</div>
          <h2 className="text-[26px] font-black tracking-[-0.02em] mt-1 text-[var(--ink)]">Top referrers per campus · cash + swag every semester.</h2>
          <p className="text-[14px] text-[var(--ink)] mt-2 leading-snug max-w-[700px]">
            Refer 25 verified students from your campus → free Newseze hoodie + 25,000 credits ($25 cash).<br />
            Hit 100 verified → $250 cash + private invite to the Newseze editorial Discord.<br />
            Hit the top-3 per campus by end of semester → guaranteed paid summer internship interview.
          </p>
        </section>

        {/* Social proof */}
        <section className="mt-8 rounded-2xl border border-[var(--line)] bg-white p-6 text-center">
          <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)]">Trusted, fair, and built for class essays</div>
          <p className="text-[14px] text-[var(--ink)] mt-2 max-w-[640px] mx-auto">
            Every story is rated for trust, balance, and bias. Use Newseze AI to write a class essay that cites all sides
            of an issue — and watch your credits accumulate while you research.
          </p>
        </section>
      </main>
      <Footer />
    </>
  );
}
